import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { resolveMediaUrl } from '../../utils/docsApi';

const EMPTY = {
  name: '',
  styleName: '',
  category: 'Work',
  description: '',
  content: '<p></p>',
  enabled: true,
  sortOrder: 200,
};

const DocTemplatesAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [thumbFile, setThumbFile] = useState(null);

  const load = async () => {
    setError('');
    try {
      const res = await axiosInstance.get('/api/admin/docs/templates');
      if (res.data?.success) {
        setTemplates(res.data.data || []);
        setCategories(res.data.categories || []);
      } else {
        setError(res.data?.message || 'Could not load templates');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load templates');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing('new');
    setForm(EMPTY);
    setThumbFile(null);
    setMessage('');
  };

  const openEdit = (row) => {
    setEditing(row.slug || row._id);
    setForm({
      name: row.name || '',
      styleName: row.styleName || '',
      category: row.category || 'Work',
      description: row.description || '',
      content: row.content || '<p></p>',
      enabled: row.enabled !== false,
      sortOrder: row.sortOrder || 100,
      thumbnailUrl: row.thumbnailUrl || '',
    });
    setThumbFile(null);
    setMessage('');
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY);
    setThumbFile(null);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        name: form.name,
        styleName: form.styleName,
        category: form.category,
        description: form.description,
        content: form.content,
        enabled: form.enabled,
        sortOrder: Number(form.sortOrder) || 200,
      };
      const res =
        editing === 'new'
          ? await axiosInstance.post('/api/admin/docs/templates', payload)
          : await axiosInstance.put(`/api/admin/docs/templates/${editing}`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');

      const id = res.data.data?.slug || res.data.data?._id || editing;
      if (thumbFile && id && id !== 'new') {
        const body = new FormData();
        body.append('thumbnail', thumbFile);
        await axiosInstance.post(`/api/admin/docs/templates/${id}/thumbnail`, body);
      }

      setMessage(editing === 'new' ? 'Template created' : 'Template saved');
      closeForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save template');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row) => {
    try {
      await axiosInstance.put(`/api/admin/docs/templates/${row.slug || row._id}`, {
        enabled: !row.enabled,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update template');
    }
  };

  const remove = async (row) => {
    if (row.isBuiltIn) return;
    if (!window.confirm(`Delete template “${row.name}”? Existing documents are not affected.`)) return;
    try {
      await axiosInstance.delete(`/api/admin/docs/templates/${row.slug || row._id}`);
      setMessage('Template deleted');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not delete template');
    }
  };

  return (
    <section className="admin-card admin-docs-templates-card">
      <div className="admin-section-title">
        <h3>
          Docs templates
          <span className="admin-user-count">({templates.length})</span>
        </h3>
        <button type="button" onClick={openCreate}>
          + New template
        </button>
      </div>
      <p className="admin-tpl-hint">
        Creating a document copies template HTML. Editing a template never changes documents that
        already exist.
      </p>
      {message && <div className="admin-alert success">{message}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Category</th>
              <th>Status</th>
              <th>Uses</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((row) => (
              <tr key={row._id || row.slug}>
                <td>
                  <strong>{row.name}</strong>
                  <span>
                    {row.styleName || '—'} · {row.slug}
                    {row.isBuiltIn ? ' · built-in' : ''}
                  </span>
                </td>
                <td>{row.category}</td>
                <td>{row.enabled ? 'Enabled' : 'Disabled'}</td>
                <td>{row.useCount || 0}</td>
                <td>
                  <div className="admin-actions">
                    <button type="button" onClick={() => openEdit(row)}>
                      Edit
                    </button>
                    <button type="button" onClick={() => toggleEnabled(row)}>
                      {row.enabled ? 'Disable' : 'Enable'}
                    </button>
                    {!row.isBuiltIn && (
                      <button type="button" className="danger" onClick={() => remove(row)}>
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!templates.length && (
              <tr>
                <td colSpan={5} className="admin-empty">
                  No templates yet. They are seeded on first Docs visit.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="admin-group-overlay" role="presentation" onClick={closeForm}>
          <div
            className="admin-group-modal admin-tpl-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-group-modal-head">
              <h3>{editing === 'new' ? 'Create template' : 'Edit template'}</h3>
              <button type="button" className="admin-group-close" onClick={closeForm} aria-label="Close">
                x
              </button>
            </div>
            <label className="admin-group-field">
              <span>Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Resume"
              />
            </label>
            <label className="admin-group-field">
              <span>Style name</span>
              <input
                type="text"
                value={form.styleName}
                onChange={(e) => setForm((f) => ({ ...f, styleName: e.target.value }))}
                placeholder="Professional"
              />
            </label>
            <label className="admin-group-field">
              <span>Category</span>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {(categories.length ? categories : ['Work']).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-group-field">
              <span>Description</span>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>
            <label className="admin-group-field">
              <span>Sort order</span>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              />
            </label>
            <label className="admin-group-field admin-tpl-check">
              <span>
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />{' '}
                Enabled in gallery
              </span>
            </label>
            <label className="admin-group-field">
              <span>Thumbnail</span>
              <input type="file" accept="image/*" onChange={(e) => setThumbFile(e.target.files?.[0] || null)} />
            </label>
            {form.thumbnailUrl && (
              <img className="admin-tpl-thumb" src={resolveMediaUrl(form.thumbnailUrl)} alt="" />
            )}
            <label className="admin-group-field">
              <span>Content (HTML)</span>
              <textarea
                rows={10}
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              />
            </label>
            <div className="admin-group-actions">
              <button type="button" className="admin-group-cancel" onClick={closeForm}>
                Cancel
              </button>
              <button type="button" className="admin-group-save" onClick={save} disabled={saving || !form.name.trim()}>
                {saving ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DocTemplatesAdmin;
